if (!customElements.get('section-product-recommendations')) {
    const recommendationHTMLCache = new Map();
    const normalized = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const words = (value) => normalized(value).split(' ').filter(Boolean);
    const colors = new Set(words('nero nera neri nere black blk bianco bianca bianchi bianche white wht brown marrone marroni grey gray grigio grigia pink rosa blue blu verde green red rosso rossa beige cream panna ivory crema ecru sand sabbia nude nocciola ice ghiaccio ash cenere oliva olive yellow giallo gialla lavender lilla purple viola navy khaki bordeaux burgundy fucsia fuchsia arancio orange blackwhite'));
    const generic = new Set(words('lasgym uomo donna man men woman women unisex girl boy ragazza ragazzo over oversized oversize premium crop cropped classic classico classica basic basics luxury gym gymwear wear sport sports sportswear retro vintage new nuovo nuova collection collezione fitness bodybuilding fit loose slim regular wide super big logo small mini micro cotton cotone felpa felpe hoodie hoodies sweatshirt sweatshirts t shirt shirts tshirt tshirts tee tees maglia maglie canotta canotte tank tanks top tops pantalone pantaloni pant pants trouser trousers jogger joggers baggy legging leggings short shorts pantaloncino pantaloncini bermuda cappuccio zip full half long sleeve sleeves con senza e and in di da del della the a an all made italy italia madeinitaly taglio vestibilita ampia ampio stampa stampata stampato tape'));
    const male = new Set(['uomo', 'uomini', 'man', 'men', 'mens', 'maschile']);
    const female = new Set(['donna', 'donne', 'woman', 'women', 'womens', 'femminile']);
    const excludedTags = new Set(['hidden', 'nascosto', 'nascosta', 'gift', 'gift card', 'giftcard', 'buono regalo', 'exclude from recommendations', 'exclude recommendations', 'no recommendations', 'hidden recommendation', 'hidden recommendations', 'recommendations exclude', 'escludi raccomandazioni']);
    const canonical = (word) => word === 'ghotic' ? 'gothic' : word;
    const colorGroups = ['nero nera neri nere black blk', 'bianco bianca bianchi bianche white wht', 'brown marrone marroni', 'grey gray grigio grigia', 'pink rosa', 'blue blu navy', 'verde green', 'red rosso rossa', 'beige cream panna ivory crema ecru', 'yellow giallo gialla', 'lavender lilla purple viola', 'bordeaux burgundy', 'fucsia fuchsia', 'arancio orange'].map(words);
    const family = (product) => new Set(words(product.title).map(canonical).filter((word) => word.length >= 4 && !generic.has(word) && !colors.has(word)));
    const titleKey = (product) => words(product.title).filter((word) => !colors.has(word) && !/^\d+$/.test(word)).map(canonical).join(' ');

    function audience(product) {
        const tags = Array.isArray(product.tags) ? product.tags.map(normalized) : [];
        const collectionWords = (Array.isArray(product.collections) ? product.collections : []).flatMap(words);
        const markers = tags.concat(collectionWords);
        const genders = new Set();
        if (markers.includes('unisex')) genders.add('unisex');
        if (markers.some((marker) => male.has(marker))) genders.add('male');
        if (markers.some((marker) => female.has(marker))) genders.add('female');
        return genders;
    }

    function compatible(current, candidate) {
        const target = audience(current);
        const proposed = audience(candidate);
        if (!proposed.size) return false;
        if (proposed.has('unisex')) return true;
        if (!target.size) return false;
        return target.has('unisex') || Array.from(target).some((gender) => proposed.has(gender));
    }

    function classify(value) {
        const title = normalized(value);
        const categories = [
            ['shorts', 'bottom', /\b(shorts?|pantaloncini?|bermuda)\b/],
            ['leggings', 'bottom', /\b(leggings?|leggins)\b/],
            ['pants', 'bottom', /\b(pantalon[ei]|pants?|trousers?|joggers?)\b/],
            ['sweatshirt', 'top', /\b(felp[ae]|hoodies?|sweatshirts?)\b/],
            ['longsleeve', 'top', /\b(long sleeves?|manica lunga|maniche lunghe)\b/],
            ['tshirt', 'top', /\b(t shirts?|tshirts?|tees?)\b/],
            ['tank', 'top', /\b(canott[ae]|tanks?|tops?)\b/],
            ['body', 'top', /\b(body|bodysuit)\b/],
            ['jacket', 'outerwear', /\b(giacc[ah]e?|giubbotto|jackets?|bombers?)\b/],
            ['hat', 'accessory', /\b(cappell[oi]|caps?|hats?|beanie)\b/],
            ['bag', 'accessory', /\b(bors[ae]|bags?|zain[oi]|backpacks?)\b/],
            ['shoes', 'footwear', /\b(scarp[ae]|sneakers?|shoes?|sandals?)\b/],
            ['socks', 'accessory', /\b(calz[ae]|calzin[oi]|socks?)\b/],
            ['tracksuit', 'set', /\b(tut[ae]|tracksuits?|completo)\b/]
        ];
        const match = categories.find((entry) => entry[2].test(title));
        return match ? { category: match[0], group: match[1] } : null;
    }

    function category(product) {
        return classify(product.title) || classify(product.type) || { category: '', group: '' };
    }

    function createdAt(product) {
        const numeric = Number(product.createdAt);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : (Date.parse(product.createdAt) || 0) / 1000;
    }

    function eligible(current, product) {
        if (!product || !product.id || String(product.id) === String(current.id) || product.available !== true || Number(product.price) <= 0 || !Number.isFinite(Number(product.price))) return false;
        if (product.excluded === true || product.giftCard === true || /\b(gift card|giftcard|buono regalo)\b/.test(normalized(product.title + ' ' + product.type))) return false;
        if ((Array.isArray(product.tags) ? product.tags : []).some((tag) => excludedTags.has(normalized(tag)))) return false;
        return compatible(current, product);
    }

    function phraseOverlap(current, product, distinctive) {
        const currentWords = words(current.title).map(canonical);
        const candidateWords = words(product.title).map(canonical);
        const pairs = candidateWords.slice(1).map((word, index) => candidateWords[index] + ' ' + word);
        return currentWords.slice(1).filter((word, index) => (distinctive.has(word) || distinctive.has(currentWords[index])) && pairs.includes(currentWords[index] + ' ' + word)).length;
    }

    function colorOverlap(current, product) {
        const currentWords = words(current.title);
        const candidateWords = words(product.title);
        return colorGroups.some((group) => group.some((color) => currentWords.includes(color)) && group.some((color) => candidateWords.includes(color))) ? 1 : 0;
    }

    function rankRecommendations(current, candidates, limit) {
        const currentFamily = family(current);
        const currentCategory = category(current);
        const ranked = [];
        for (const [index, entry] of candidates.entries()) {
            const product = entry.product;
            if (!eligible(current, product)) continue;
            const candidateCategory = category(product);
            const shared = Array.from(family(product)).filter((token) => currentFamily.has(token)).length;
            const sameCategory = !!currentCategory.category && currentCategory.category === candidateCategory.category;
            const complements = (currentCategory.group === 'top' && candidateCategory.group === 'bottom') || (currentCategory.group === 'bottom' && candidateCategory.group === 'top');
            let matchTier;
            if (entry.curated) matchTier = 0;
            else if (shared && complements) matchTier = 1;
            else if (shared && sameCategory) matchTier = 2;
            else if (sameCategory) matchTier = 3;
            else continue;
            ranked.push({ ...entry, matchTier, shared, phrase: shared ? phraseOverlap(current, product, currentFamily) : 0, color: shared ? colorOverlap(current, product) : 0, index });
        }
        ranked.sort((left, right) => left.matchTier - right.matchTier || (left.matchTier === 0 ? left.index - right.index : right.shared - left.shared || right.phrase - left.phrase || right.color - left.color || createdAt(right.product) - createdAt(left.product) || left.index - right.index));
        const ids = new Set([String(current.id)]);
        const titles = new Set([titleKey(current)]);
        const unique = ranked.filter((entry) => {
            const id = String(entry.product.id);
            const key = titleKey(entry.product);
            if (ids.has(id) || !key || titles.has(key)) return false;
            ids.add(id);
            titles.add(key);
            return true;
        });
        const hasAlternatives = unique.some((entry) => entry.matchTier >= 2);
        const coordinatedLimit = hasAlternatives ? Math.max(1, Math.min(4, Math.floor(limit / 2))) : limit;
        let coordinatedCount = 0;
        return unique.filter((entry) => entry.matchTier !== 1 || ++coordinatedCount <= coordinatedLimit).slice(0, limit);
    }

    class TG_SectionProductRecommendation extends HTMLElement {
        static rankCandidates(current, candidates, limit = 8) {
            return rankRecommendations(current, candidates, limit);
        }

        static needsMoreCandidates(current, candidates, limit = 8) {
            const ranked = rankRecommendations(current, candidates, limit);
            return ranked.length < limit || !ranked.some((entry) => entry.matchTier === 1);
        }

        connectedCallback() {
            this.element = this;
            try {
                this.options = JSON.parse(this.getAttribute('data-section-settings'));
                if (!this.options) throw new Error('Missing recommendation settings');
            } catch (_) {
                this._hide();
                return;
            }
            if (window.Shopify && window.Shopify.visualPreviewMode) return;
            this._controller = new AbortController();
            this._generation = (this._generation || 0) + 1;
            this._started = false;
            this.hidden = false;
            this.style.removeProperty('display');
            this.setAttribute('aria-busy', 'true');
            if (window.Shopify && window.Shopify.designMode) {
                this._timer = setTimeout(() => this._start(), 1000);
            } else if (this.options.recommendationsStrategy === 'coordinated-new-arrivals' && 'IntersectionObserver' in window) {
                this._observer = new IntersectionObserver((entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) this._start();
                }, { rootMargin: '800px 0px' });
                this._observer.observe(this);
            } else {
                this._start();
            }
        }

        disconnectedCallback() {
            if (this._controller) this._controller.abort();
            if (this._observer) this._observer.disconnect();
            clearTimeout(this._timer);
            if (this.carousel) this.carousel.destroy();
            this.carousel = null;
            this._generation = (this._generation || 0) + 1;
        }

        async _start() {
            if (this._started || !this.isConnected) return;
            this._started = true;
            if (this._observer) this._observer.disconnect();
            const generation = this._generation;
            const signal = this._controller.signal;
            try {
                if (this.options.recommendationsStrategy === 'coordinated-new-arrivals') {
                    await this._loadContextualRecommendations(signal, generation);
                } else {
                    await this._loadRecommendations(signal, generation);
                }
            } catch (error) {
                if (error.name !== 'AbortError' && this._active(signal, generation)) this._hide();
            } finally {
                if (this._active(signal, generation)) this.removeAttribute('aria-busy');
            }
        }

        _active(signal, generation) {
            return !signal.aborted && this.isConnected && generation === this._generation;
        }

        _hide() {
            this.hidden = true;
            this.style.display = 'none';
            this.removeAttribute('aria-busy');
        }

        async _requestHTML(url, signal) {
            if (recommendationHTMLCache.has(url)) return recommendationHTMLCache.get(url);
            const response = await fetch(url, { signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('Recommendations unavailable');
            const content = await response.text();
            if (!signal.aborted) {
                recommendationHTMLCache.set(url, content);
                if (recommendationHTMLCache.size > 24) recommendationHTMLCache.delete(recommendationHTMLCache.keys().next().value);
            }
            return content;
        }

        _readCandidates(container, selector, curated = false) {
            return Array.from(container.querySelectorAll(selector)).flatMap((template) => {
                try {
                    const product = JSON.parse(template.getAttribute('data-product'));
                    if (!product || !template.content.querySelector('[data-lg-recommendation-card]')) return [];
                    return [{ product, template, curated, kind: template.getAttribute('data-lg-recommendation-kind') || 'related' }];
                } catch (_) {
                    return [];
                }
            });
        }

        async _fetchCandidatePage(source, page, signal) {
            const url = new URL(source, window.location.origin);
            if (url.origin !== window.location.origin || !/\/collections\//.test(url.pathname)) return { candidates: [], hasNext: false };
            url.searchParams.set('section_id', 'product-recommendations');
            url.searchParams.set('sort_by', 'created-descending');
            url.searchParams.set('page', String(page));
            const content = await this._requestHTML(url.href, signal);
            const result = new DOMParser().parseFromString(content, 'text/html');
            return {
                candidates: this._readCandidates(result, 'template[data-lg-recommendation-candidate]'),
                hasNext: result.querySelector('[data-lg-recommendation-page]')?.getAttribute('data-has-next') === 'true'
            };
        }

        async _loadContextualRecommendations(signal, generation) {
            const product = this.options.product;
            if (!product || !product.id) return this._hide();
            const limit = Math.min(16, Math.max(1, Number(this.options.recommendationsCount) || 8));
            const manualOnly = normalized(this.options.manualMode).replace(/ /g, '') === 'onlymanual';
            const curated = this._readCandidates(this, 'template[data-lg-recommendation-curated]', true);
            const candidates = manualOnly ? curated.filter((entry) => entry.kind !== 'complementary') : curated;
            const sources = Array.from(new Set(Array.isArray(this.options.candidateSources) ? this.options.candidateSources : [])).slice(0, 2);
            if (!manualOnly && sources.length) {
                const firstPages = await Promise.allSettled(sources.map((source) => this._fetchCandidatePage(source, 1, signal)));
                if (!this._active(signal, generation)) return;
                firstPages.forEach((result) => {
                    if (result.status === 'fulfilled') candidates.push(...result.value.candidates);
                });
                if (TG_SectionProductRecommendation.needsMoreCandidates(product, candidates, limit)) {
                    const nextSources = sources.filter((_, index) => firstPages[index].status === 'fulfilled' && firstPages[index].value.hasNext);
                    const secondPages = await Promise.allSettled(nextSources.map((source) => this._fetchCandidatePage(source, 2, signal)));
                    if (!this._active(signal, generation)) return;
                    secondPages.forEach((result) => {
                        if (result.status === 'fulfilled') candidates.push(...result.value.candidates);
                    });
                }
            }
            if (!this._active(signal, generation)) return;
            this._renderCandidates(rankRecommendations(product, candidates, limit));
        }

        _renderCandidates(candidates) {
            if (!candidates.length) return this._hide();
            const list = this.querySelector('.ProductRecommendations__Body .ProductList');
            const carousel = this.options.layout === 'carousel';
            const wrapper = carousel ? list?.querySelector('.CarouselWrapper') : list;
            if (!wrapper) return this._hide();
            const mobile = Math.min(4, Math.max(1, Number(this.options.mobileItemsPerRow) || 2));
            const desktop = Math.min(6, Math.max(1, Number(this.options.desktopItemsPerRow) || 4));
            const fragment = document.createDocumentFragment();
            candidates.forEach((entry) => {
                const item = entry.template.content.querySelector('[data-lg-recommendation-card]').cloneNode(true);
                item.classList.add(carousel ? 'Carousel__Item' : 'Grid__Item', '1/' + mobile + '--small', '1/' + desktop + '--medium-up');
                item.setAttribute('data-lg-recommendation-product-id', entry.product.id);
                item.setAttribute('data-lg-recommendation-reason', entry.curated ? 'curated' : entry.matchTier === 1 ? 'coordinated' : 'similar');
                fragment.appendChild(item);
            });
            wrapper.replaceChildren(fragment);
            list.classList.add('ProductList--hasProduct');
            this.setAttribute('data-lg-recommendation-count', candidates.length);
            this._initializeCarousel();
        }

        _initializeCarousel() {
            if (this.options.layout === 'carousel' && !this.carousel) {
                const carousel = this.querySelector('[data-carousel-config]');
                if (carousel && window.themegoal?.components?.Carousel) this.carousel = new window.themegoal.components.Carousel(carousel);
            }
        }

        async _loadRecommendations(signal, generation) {
            const url = new URL(window.routes.productRecommendationsUrl, window.location.origin);
            url.searchParams.set('section_id', this.options.sectionId);
            url.searchParams.set('product_id', this.options.productId);
            url.searchParams.set('limit', this.options.recommendationsCount);
            url.searchParams.set('intent', this.options.recommendationsType);
            const content = await this._requestHTML(url.href, signal);
            if (!this._active(signal, generation)) return;
            const container = new DOMParser().parseFromString(content, 'text/html');
            const body = container.querySelector('.ProductRecommendations__Body');
            if (!body || !container.querySelector('.ProductList--hasProduct')) return this._hide();
            this.querySelector('.ProductRecommendations__Body').innerHTML = body.innerHTML;
            this._initializeCarousel();
        }
    }

    customElements.define('section-product-recommendations', TG_SectionProductRecommendation);
}
